import { useState, useEffect, useRef } from "react";
import { changeDepositStatus } from "../../services/apiService";
import { Pagination } from "react-bootstrap";
import { TbFileDownload } from "react-icons/tb";
import { PiSealPercentBold } from "react-icons/pi";
import { toast } from "react-toastify";
import "../../styles/admin.css";
import CustomDropdown from "../utilComponents/customDropDown.js";
import Loader from "../utilComponents/loader";
import ShimmerLoader from "../utilComponents/shimmer.js";
import { Tooltip } from "bootstrap";
import { useAllDepositRequests } from "../../services/hooks/getAdminapis";

const DepositRequests = ({ searchQuery }) => {
  const [depositRequests, setDepositRequests] = useState([]);
  const [loadingButtons, setLoadingButtons] = useState({});
  const [modalImage, setModalImage] = useState(null); // State to manage modal image
  const [isModalOpen, setIsModalOpen] = useState(false); // State to manage modal visibility
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Number of items per page

  const { data: allDepositRequests, isLoading } = useAllDepositRequests();
  // console.log("All Deposit Requests:", allDepositRequests);

  useEffect(() => {
    if (
      allDepositRequests &&
      typeof allDepositRequests === "object" &&
      Array.isArray(allDepositRequests.depositRequests)
    ) {
      setDepositRequests(allDepositRequests.depositRequests.reverse());
      // console.log("Deposit Requests:", allDepositRequests.depositRequests);
    }
  }, [allDepositRequests]);

  // tooltip code===================================
  const buttonRef = useRef(null);
  useEffect(() => {
    if (buttonRef.current) {
      const tooltip = new Tooltip(buttonRef.current, {
        placement: "top",
        delay: { show: 1000, hide: 100 },
      });

      return () => {
        tooltip.dispose();
      };
    }
  }, []);

  // **Filter deposit requests based on searchQuery**
  const filteredRequests = depositRequests.filter(
    (request) =>
      request.AccountID.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.deposit_mode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.amount.toString().includes(searchQuery) ||
      request.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDepositStatusChange = async (
    accountID,
    id,
    updatedStatus,
    return_percentage,
    withdrawPeriod
  ) => {
    if (!updatedStatus) {
      toast.info("Please select a status");
      return;
    }
    setLoadingButtons((prev) => ({ ...prev, [id]: true }));
    try {
      //from apiService.js
      await changeDepositStatus(
        accountID,
        id,
        updatedStatus,
        return_percentage,
        withdrawPeriod
      );
      toast.success("Deposit status updated successfully");
    } catch (err) {
      toast.error(err.response.data.message || "Error updating deposit status");
      console.error(err);
    } finally {
      setLoadingButtons((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleStatusChange = (id, value) => {
    setDepositRequests((prevRequests) =>
      prevRequests.map((request) =>
        request._id === id ? { ...request, status: value } : request
      )
    );
  };

  const openModal = (image) => {
    setModalImage(image); // Set the image source for the modal
    setIsModalOpen(true); // Open the modal
  };

  const closeModal = () => {
    setIsModalOpen(false); // Close the modal
    setModalImage(null); // Clear the image source
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const downloadCSV = () => {
  const headers = [
    "AccountID",
    "Account Type",
    "Deposit Mode",
    "Image Proof",
    "Amount",
    "WithdrawPeriod (user wants)",
    "Time Period",
    "Status",
    "Returns (%)",
    "Set WithdrawPeriod",
  ];

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '""';
    const str = value.toString();
    return `"${str.replace(/"/g, '""')}"`;
  };

  const rows = filteredRequests.map((req) => [
    escapeCSV(req.AccountID),
    escapeCSV(req.AccountType || "N/A"),
    escapeCSV(req.deposit_mode),
    escapeCSV(req.image_proof ? req.image_proof : "No Proof Uploaded"),
    escapeCSV(req.amount),
    escapeCSV(req.withdrawPeriod || "yearly"),
    escapeCSV(req.time_period ? `${req.time_period} Months` : "N/A"),
    escapeCSV(req.status),
    escapeCSV(req.return_percentage || 0),
    escapeCSV(req.withdrawPeriod || "yearly"),
  ]);

  const csvContent = [headers.map(escapeCSV), ...rows]
    .map((row) => row.join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "deposit_requests.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  // Calculate total pages for deposit requests
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredRequests.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const pending = depositRequests.filter(
    (request) => request.status === "Pending"
  ).length;

  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to manage popup visibility

  const openPopup = () => {
    setIsPopupOpen(true); // Open the popup
  };

  const closePopup = () => {
    setIsPopupOpen(false); // Close the popup
  };

  return (
    <>
      {isLoading && <Loader />}
      <div>
        <h2>Deposit Requests</h2>
        <div className="csv">
          {pending === 0 ? (
            <p style={{ color: "green" }}>No Pending Requests</p>
          ) : (
            <p style={{ color: "red" }}>Total Pending Requests: {pending}</p>
          )}

          <div style={{ alignSelf: "flex-end" }}>
            <button
              onClick={openPopup}
              className="downloadBtn"
              ref={buttonRef}
              title="Reutrns-%">
              <PiSealPercentBold />
            </button>
            <button
              onClick={downloadCSV}
              className="downloadBtn"
              ref={buttonRef}
              title="Download-CSV">
              <TbFileDownload />
            </button>
          </div>
        </div>
        <div className="scrollable-table">
          <table>
            <thead>
              <tr>
                <th>Account ID</th>
                <th>Account Type</th>
                <th>Deposit Mode</th>
                <th>Image Proof</th>
                <th>Amount</th>
                <th>WithdrawPeriod <small>(user wants)</small></th>
                <th>Time Period</th>
                <th>Status</th>
                <th>Returns</th>
                <th>Set WithdrawPeriod</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(currentItems) && currentItems.length > 0 ? (
                currentItems.map((request) => (
                  <tr key={request._id}>
                    {/* <td>{request.AccountID}</td> */}
                        {request.oldAge ? (
                      <td style={{ position: "relative" }}>
                        <div className="corner-ribbon">OldAge Acc</div>

                        {request.AccountID}
                      </td>
                    ) : (
                      <td>{request.AccountID}</td>
                    )}
                    <td>{request?.AccountType || "N/A"}</td>
                    <td>{request.deposit_mode}</td>
                    <td>
                      {request.image_proof ? (
                        <img
                          src={request.image_proof}
                          alt="Proof"
                          className="zoom-image"
                          style={{
                            maxWidth: "100px",
                            maxHeight: "100px",
                            cursor: "pointer",
                          }}
                          onClick={() => openModal(request.image_proof)}
                        />
                      ) : (
                        "No Proof Uploaded"
                      )}
                    </td>
                    <td>₹{request.amount}</td>
                    <td>{request.withdrawPeriod}</td>
                    <td>
                      {request.time_period
                        ? `${request.time_period} Months`
                        : "N/A"}
                    </td>
                    <td>{request.status}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          minWidth: "160px",
                        }}>
                        {/* Number Input for Point Values */}
                        <input
                          type="number"
                          min="0"
                          max="20"
                          step="0.1"
                          value={request.return_percentage || 0}
                          onChange={(e) => {
                            let value = parseFloat(e.target.value) || 0;
                            if (value < 0) value = 0;
                            if (value > 20) value = 20;

                            setDepositRequests((prev) =>
                              prev.map((r) =>
                                r._id === request._id
                                  ? {
                                      ...r,
                                      return_percentage: value,
                                    }
                                  : r
                              )
                            );
                          }}
                          style={{
                            width: "80px",
                            padding: "4px",
                            fontSize: "0.9rem",
                          }}
                        />

                        {/* Range Input in Points (with decimal step) */}
                        <input
                          type="range"
                          min="0"
                          max="20"
                          step="0.1"
                          value={request.return_percentage || 0}
                          onChange={(e) =>
                            setDepositRequests((prev) =>
                              prev.map((r) =>
                                r._id === request._id
                                  ? {
                                      ...r,
                                      return_percentage: parseFloat(
                                        e.target.value
                                      ),
                                    }
                                  : r
                              )
                            )
                          }
                        />
                      </div>
                    </td>
                    {/* select withdraw period */}
                    <td>
                      <div>
                        <CustomDropdown
                          options={["yearly", "half-yearly", "quarterly"]}
                          selected={request.withdrawPeriod || "yearly"}
                          onSelect={(value) =>
                            setDepositRequests((prevRequests) =>
                              prevRequests.map((r) =>
                                r._id === request._id
                                  ? { ...r, withdrawPeriod: value }
                                  : r
                              )
                            )
                          }
                        />
                      </div>
                    </td>
                    {/* action */}
                    <td>
                      <div>
                        <CustomDropdown
                          options={["Approved", "Declined"]}
                          selected={request.status}
                          onSelect={(value) =>
                            handleStatusChange(request._id, value)
                          }
                        />
                      </div>
                      <button
                        onClick={() =>
                          handleDepositStatusChange(
                            request.AccountID,
                            request._id,
                            request.status,
                            request.return_percentage,
                            request.withdrawPeriod
                          )
                        }
                        disabled={loadingButtons[request._id]}>
                        {loadingButtons[request._id]
                          ? "Updating..."
                          : "UpdateStatus"}
                      </button>
                    </td>
                  </tr>
                ))
              ) : isLoading ? (
                <tr>
                  <td colSpan="11">
                    <ShimmerLoader width="100%" height="30px" />
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan="11">No deposit requests found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            {[...Array(totalPages)].map((_, pageIndex) => (
              <Pagination.Item
                key={pageIndex}
                active={currentPage === pageIndex + 1}
                onClick={() => handlePageChange(pageIndex + 1)}>
                {pageIndex + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </div>
        {isModalOpen && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.modal}>
              <button style={modalStyles.closeButton} onClick={closeModal}>
                x
              </button>
              <img
                src={`${modalImage}`}
                alt="Modal Image"
                style={{ width: "100%", height: "85vh" }}
              />
            </div>
          </div>
        )}
        {isPopupOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button className="modal-close-button" onClick={closePopup}>
                x
              </button>
              <div>
                <h3>Classic Account</h3>
                <p>Investment Range: ₹2.5 Lakhs - ₹4.99 Lakhs</p>
                <p>Potential Returns: Up to 12% - 15% p.a.</p>
                <h3>Premium Account</h3>
                <p>Investment Range: ₹5 Lakhs - ₹9.99 Lakhs</p>
                <p>Potential Returns: Up to 15% - 18% p.a.</p>
                <h3>Business Account</h3>
                <p>Investment Range: ₹10 Lakhs - ₹14.99 Lakhs</p>
                <p>Potential Returns: Up to 18% - 24% p.a.</p>
                <h3>Enterprise Account</h3>
                <p>Investment Range: ₹15 Lakhs and above</p>
                <p>Potential Returns: Up to 24% - 30% p.a.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Modal styles
const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    position: "relative",
    maxWidth: "90%",
    maxHeight: "90%",
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)",
  },
  closeButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    padding: "5px 10px",
    backgroundColor: "red",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
  },
};

// Add zoom effect styles
const zoomImageStyle = {
  transition: "transform 0.3s ease", // Smooth zoom transition
};

export default DepositRequests;
